/**
 * Built-in workout templates — curated by DUEL.
 *
 * Pro users get access to all templates.
 * Each template: name, description, category, exercises with target sets/reps.
 */

export interface BuiltInTemplateExercise {
  exerciseName: string;
  targetSets: number;
  targetReps: string; // e.g. "8-12", "5", "30s"
  notes?: string;
}

export interface BuiltInTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  exercises: BuiltInTemplateExercise[];
}

export const BUILT_IN_TEMPLATES: BuiltInTemplate[] = [
  // ── Push/Pull/Legs (3) ──
  {
    id: 'duel_push',
    name: 'Push Day',
    description: 'Chest, shoulders & triceps',
    category: 'Push/Pull/Legs',
    icon: '💪',
    exercises: [
      { exerciseName: 'Bench Press', targetSets: 4, targetReps: '6-8' },
      { exerciseName: 'Overhead Press', targetSets: 3, targetReps: '8-10' },
      { exerciseName: 'Incline Dumbbell Press', targetSets: 3, targetReps: '10-12' },
      { exerciseName: 'Lateral Raise', targetSets: 3, targetReps: '12-15' },
      { exerciseName: 'Tricep Pushdown', targetSets: 3, targetReps: '10-12' },
      { exerciseName: 'Overhead Tricep Extension', targetSets: 2, targetReps: '12-15' },
    ],
  },
  {
    id: 'duel_pull',
    name: 'Pull Day',
    description: 'Back & biceps',
    category: 'Push/Pull/Legs',
    icon: '🏋️',
    exercises: [
      { exerciseName: 'Deadlift', targetSets: 4, targetReps: '5' },
      { exerciseName: 'Pull-Up', targetSets: 3, targetReps: '6-10' },
      { exerciseName: 'Barbell Row', targetSets: 3, targetReps: '8-10' },
      { exerciseName: 'Face Pull', targetSets: 3, targetReps: '15-20' },
      { exerciseName: 'Barbell Curl', targetSets: 3, targetReps: '10-12' },
      { exerciseName: 'Hammer Curl', targetSets: 2, targetReps: '12-15' },
    ],
  },
  {
    id: 'duel_legs',
    name: 'Leg Day',
    description: 'Quads, hamstrings & glutes',
    category: 'Push/Pull/Legs',
    icon: '🦵',
    exercises: [
      { exerciseName: 'Back Squat', targetSets: 4, targetReps: '6-8' },
      { exerciseName: 'Romanian Deadlift', targetSets: 3, targetReps: '8-10' },
      { exerciseName: 'Leg Press', targetSets: 3, targetReps: '10-12' },
      { exerciseName: 'Walking Lunge', targetSets: 3, targetReps: '10-12', notes: 'per leg' },
      { exerciseName: 'Leg Curl', targetSets: 3, targetReps: '12-15' },
      { exerciseName: 'Calf Raise', targetSets: 4, targetReps: '15-20' },
    ],
  },

  // ── Upper/Lower Split (2) ──
  {
    id: 'duel_upper',
    name: 'Upper Body',
    description: 'Chest, back, shoulders & arms',
    category: 'Upper/Lower',
    icon: '🔝',
    exercises: [
      { exerciseName: 'Bench Press', targetSets: 4, targetReps: '6-8' },
      { exerciseName: 'Barbell Row', targetSets: 4, targetReps: '6-8' },
      { exerciseName: 'Overhead Press', targetSets: 3, targetReps: '8-10' },
      { exerciseName: 'Pull-Up', targetSets: 3, targetReps: '8-10' },
      { exerciseName: 'Dumbbell Curl', targetSets: 2, targetReps: '10-12' },
      { exerciseName: 'Tricep Pushdown', targetSets: 2, targetReps: '10-12' },
    ],
  },
  {
    id: 'duel_lower',
    name: 'Lower Body',
    description: 'Squats, deadlifts & accessories',
    category: 'Upper/Lower',
    icon: '🦿',
    exercises: [
      { exerciseName: 'Back Squat', targetSets: 4, targetReps: '6-8' },
      { exerciseName: 'Romanian Deadlift', targetSets: 3, targetReps: '8-10' },
      { exerciseName: 'Bulgarian Split Squat', targetSets: 3, targetReps: '10-12', notes: 'per leg' },
      { exerciseName: 'Leg Curl', targetSets: 3, targetReps: '12-15' },
      { exerciseName: 'Calf Raise', targetSets: 4, targetReps: '15-20' },
      { exerciseName: 'Plank', targetSets: 3, targetReps: '45-60s' },
    ],
  },

  // ── Full Body (2) ──
  {
    id: 'duel_full_body_a',
    name: 'Full Body A',
    description: 'Squat-pattern emphasis — alternate with Full Body B',
    category: 'Full Body',
    icon: '🅰️',
    exercises: [
      { exerciseName: 'Back Squat', targetSets: 3, targetReps: '5', notes: 'Linear progression — add 5 lb each session' },
      { exerciseName: 'Bench Press', targetSets: 3, targetReps: '5', notes: 'Linear progression' },
      { exerciseName: 'Barbell Row', targetSets: 3, targetReps: '5', notes: 'Linear progression' },
      { exerciseName: 'Pull-Up', targetSets: 2, targetReps: '8-10' },
      { exerciseName: 'Plank', targetSets: 3, targetReps: '45-60s' },
    ],
  },
  {
    id: 'duel_full_body_b',
    name: 'Full Body B',
    description: 'Hip-hinge emphasis — alternate with Full Body A',
    category: 'Full Body',
    icon: '🅱️',
    exercises: [
      { exerciseName: 'Back Squat', targetSets: 3, targetReps: '5', notes: 'Linear progression' },
      { exerciseName: 'Overhead Press', targetSets: 3, targetReps: '5', notes: 'Linear progression' },
      { exerciseName: 'Deadlift', targetSets: 1, targetReps: '5', notes: 'Single working set — add 10 lb each session' },
      { exerciseName: 'Barbell Row', targetSets: 3, targetReps: '8-10' },
      { exerciseName: 'Romanian Deadlift', targetSets: 2, targetReps: '10-12' },
    ],
  },

  // ── 5/3/1 Programs (2) ──
  {
    id: 'duel_531_bench',
    name: '5/3/1 — Bench Focus',
    description: "Wendler 5/3/1 main lift + BBB accessory",
    category: '5/3/1',
    icon: '💪',
    exercises: [
      { exerciseName: 'Bench Press', targetSets: 3, targetReps: '5/3/1', notes: 'Week 1: 5×65/75/85% | Week 2: 3×70/80/90% | Week 3: 5/3/1×75/85/95%' },
      { exerciseName: 'Bench Press', targetSets: 5, targetReps: '10', notes: 'BBB — 50-60% of training max' },
      { exerciseName: 'Dumbbell Row', targetSets: 5, targetReps: '10-15', notes: 'Superset with BBB sets' },
      { exerciseName: 'Dumbbell Curl', targetSets: 3, targetReps: '10-15' },
      { exerciseName: 'Tricep Pushdown', targetSets: 3, targetReps: '10-15' },
    ],
  },
  {
    id: 'duel_531_squat',
    name: '5/3/1 — Squat Focus',
    description: "Wendler 5/3/1 main lift + BBB accessory",
    category: '5/3/1',
    icon: '🏋️',
    exercises: [
      { exerciseName: 'Back Squat', targetSets: 3, targetReps: '5/3/1', notes: 'Week 1: 5×65/75/85% | Week 2: 3×70/80/90% | Week 3: 5/3/1×75/85/95%' },
      { exerciseName: 'Back Squat', targetSets: 5, targetReps: '10', notes: 'BBB — 50-60% of training max' },
      { exerciseName: 'Romanian Deadlift', targetSets: 5, targetReps: '10', notes: 'Superset with BBB sets' },
      { exerciseName: 'Leg Curl', targetSets: 3, targetReps: '10-15' },
      { exerciseName: 'Calf Raise', targetSets: 3, targetReps: '15-20' },
    ],
  },

  // ── Beginner Program (1) ──
  {
    id: 'duel_starting_strength',
    name: 'Beginner — Starting Strength',
    description: "Rippetoe's novice linear progression — 3×5 compound lifts",
    category: 'Beginner',
    icon: '🌱',
    exercises: [
      { exerciseName: 'Back Squat', targetSets: 3, targetReps: '5', notes: 'Every session — add 5 lb each workout' },
      { exerciseName: 'Bench Press', targetSets: 3, targetReps: '5', notes: 'Alternates with Overhead Press — add 5 lb' },
      { exerciseName: 'Deadlift', targetSets: 1, targetReps: '5', notes: 'Single working set — add 10 lb each workout' },
    ],
  },

  // ── Core 5 Baseline (1) ──
  {
    id: 'duel_core5',
    name: 'Core 5 Baseline',
    description: 'The five lifts that determine your DUEL score',
    category: 'DUEL Specials',
    icon: '⚡',
    exercises: [
      { exerciseName: 'Bench Press', targetSets: 3, targetReps: '3-5', notes: 'Work up to heavy single or triple' },
      { exerciseName: 'Back Squat', targetSets: 3, targetReps: '3-5', notes: 'Work up to heavy single or triple' },
      { exerciseName: 'Deadlift', targetSets: 3, targetReps: '3-5', notes: 'Work up to heavy single or triple' },
      { exerciseName: 'Weighted Pull-Up', targetSets: 3, targetReps: '3-5', notes: 'Max weight for reps' },
      { exerciseName: 'Dead Hang', targetSets: 3, targetReps: 'max', notes: 'Hold as long as possible' },
    ],
  },

  // ── Powerlifting Comp Prep (1) ──
  {
    id: 'duel_powerlifting',
    name: 'Powerlifting Comp Prep',
    description: 'Heavy singles & doubles for meet day',
    category: 'Strength',
    icon: '🥇',
    exercises: [
      { exerciseName: 'Back Squat', targetSets: 5, targetReps: '1-3', notes: 'Work to heavy single' },
      { exerciseName: 'Bench Press', targetSets: 5, targetReps: '1-3', notes: 'Work to heavy single' },
      { exerciseName: 'Deadlift', targetSets: 5, targetReps: '1-3', notes: 'Work to heavy single' },
      { exerciseName: 'Pause Squat', targetSets: 3, targetReps: '3', notes: '70-80% 1RM, 2s pause' },
      { exerciseName: 'Close Grip Bench Press', targetSets: 3, targetReps: '5' },
    ],
  },

  // ── Hypertrophy Focus (1) ──
  {
    id: 'duel_hypertrophy',
    name: 'Hypertrophy Focus',
    description: 'High volume for maximum muscle growth',
    category: 'Hypertrophy',
    icon: '📈',
    exercises: [
      { exerciseName: 'Incline Dumbbell Press', targetSets: 4, targetReps: '10-12' },
      { exerciseName: 'Cable Fly', targetSets: 3, targetReps: '12-15' },
      { exerciseName: 'Lat Pulldown', targetSets: 4, targetReps: '10-12' },
      { exerciseName: 'Seated Row', targetSets: 3, targetReps: '12-15' },
      { exerciseName: 'Lateral Raise', targetSets: 4, targetReps: '15-20' },
      { exerciseName: 'Dumbbell Curl', targetSets: 3, targetReps: '12-15' },
      { exerciseName: 'Tricep Pushdown', targetSets: 3, targetReps: '12-15' },
    ],
  },

  // ── Quick 30-Minute Session (1) ──
  {
    id: 'duel_quick30',
    name: 'Quick 30-Minute Session',
    description: 'Efficient full-body workout when time is tight',
    category: 'Quick',
    icon: '⏱️',
    exercises: [
      { exerciseName: 'Back Squat', targetSets: 3, targetReps: '8' },
      { exerciseName: 'Bench Press', targetSets: 3, targetReps: '8' },
      { exerciseName: 'Barbell Row', targetSets: 3, targetReps: '8' },
      { exerciseName: 'Dumbbell Curl', targetSets: 2, targetReps: '12' },
      { exerciseName: 'Plank', targetSets: 2, targetReps: '60s' },
    ],
  },
];

/** Group templates by category for display */
export function getTemplatesByCategory(): Record<string, BuiltInTemplate[]> {
  const groups: Record<string, BuiltInTemplate[]> = {};
  for (const tmpl of BUILT_IN_TEMPLATES) {
    if (!groups[tmpl.category]) groups[tmpl.category] = [];
    groups[tmpl.category].push(tmpl);
  }
  return groups;
}
